/**
 * Breadcrumb navigation component
 */

import { Fragment } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronRight, Home } from 'lucide-react';
import { cn } from '@/lib/utils';

type BreadcrumbItem = {
  label: string;
  path: string;
};

const routeLabels: Record<string, string> = {
  '/': 'Home',
  '/wizard': 'Configuration Wizard',
  '/demos': 'Live Demos',
  '/models': 'Model Catalog',
  '/sizing': 'Infrastructure Sizing',
  '/cost-simulator': 'Cost Simulator',
  '/containers': 'Container Images',
  '/collateral': 'Documentation',
  '/installer': 'Custom Installer',
};

export function Breadcrumbs() {
  const location = useLocation();
  const pathnames = location.pathname.split('/').filter(x => x);

  // Don't show breadcrumbs on home page
  if (pathnames.length === 0) {
    return null;
  }

  const breadcrumbs: BreadcrumbItem[] = [
    { label: 'Home', path: '/' },
  ];

  let currentPath = '';
  pathnames.forEach(segment => {
    currentPath += `/${segment}`;
    breadcrumbs.push({
      label: routeLabels[currentPath] || segment,
      path: currentPath,
    });
  });

  return (
    <nav aria-label="Breadcrumb" className="py-4">
      <ol className="flex items-center space-x-2 text-sm">
        {breadcrumbs.map((crumb, index) => {
          const isLast = index === breadcrumbs.length - 1;
          const isFirst = index === 0;

          return (
            <Fragment key={crumb.path}>
              <li className="flex items-center">
                {isFirst ? (
                  <Link
                    to={crumb.path}
                    className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Home className="h-4 w-4" />
                    <span className="hidden sm:inline">{crumb.label}</span>
                  </Link>
                ) : isLast ? (
                  <span className="font-medium text-foreground">
                    {crumb.label}
                  </span>
                ) : (
                  <Link
                    to={crumb.path}
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    {crumb.label}
                  </Link>
                )}
              </li>
              {!isLast && (
                <ChevronRight className="h-4 w-4 text-muted-foreground flex-shrink-0" />
              )}
            </Fragment>
          );
        })}
      </ol>
    </nav>
  );
}
